import { Box } from '@material-ui/core'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { differenceInCalendarDays } from 'date-fns'
import { useState } from 'react'
import { useNotify } from 'react-admin'
import { Controller, useForm } from 'react-hook-form'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Wrap } from 'shared/components/Base/Wrap'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { MonthYearInput } from 'shared/components/MaskedInput/MonthYearInput'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { bgGray, lightGray2, midGray } from 'shared/theme/colors'
import { styled } from 'styled-components'

import { captureExceptionIfProduction } from '../../helpers/utils'
import {
  EmploymentHistory,
  EmploymentHistoryStatus,
  EmploymentHistoryStatusLabels,
  updateEmploymentJobDetailVerificationDates,
} from '../../models/employment'

interface UserEmploymentVerifyDatesProps {
  job: EmploymentHistory
  userId: string
  newStatus: EmploymentHistoryStatus | null
  onClose: Handler
  onSuccess: Handler
}

interface Dates {
  startDate: string
  endDate: string
}

interface Form {
  dates: Dates[]
}

export function UserEmploymentVerifyDates({
  job,
  userId,
  newStatus,
  onClose,
  onSuccess,
}: UserEmploymentVerifyDatesProps) {
  const [isLoading, setIsLoading] = useState(false)
  const notify = useNotify()
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      dates: job.details.verified_dates?.length
        ? job.details.verified_dates.map((d) => ({
            startDate: d.start_date_formatted,
            endDate: d.end_date_formatted,
          }))
        : [{ startDate: '', endDate: '' }],
    },
    mode: 'onSubmit',
  })
  const dates = watch('dates')

  async function _onSubmit(data: Form) {
    setIsLoading(true)
    try {
      await updateEmploymentJobDetailVerificationDates(job.id, userId, {
        verified_dates: data.dates.map((d) => ({ start_date: d.startDate, end_date: d.endDate })),
      })
      await onSuccess()
    } catch (error: any) {
      notify(`Unable to create dates: ${error.response.data}`, { type: 'error' })
      captureExceptionIfProduction(error)
    }
  }

  function handleAddRow() {
    setValue('dates', [...dates, { startDate: '', endDate: '' }])
  }
  function handleremoveRow(index: number) {
    setValue(
      'dates',
      dates.filter((_, i) => i !== index)
    )
  }

  return (
    <SimpleDialog isOpen maxWidth="550px" onClose={onClose} alignText="left">
      <form onSubmit={handleSubmit(_onSubmit)}>
        <Wrap width="700px" maxWidth="100%">
          <Text variant="header2" align="left" mb={3}>
            {newStatus ? (
              <>
                Change verification status to <br /> {EmploymentHistoryStatusLabels[newStatus]}?
              </>
            ) : (
              <>Edit verification dates</>
            )}
          </Text>

          <Text variant="header4" align="left" mb={1}>
            Verified employment period
          </Text>

          {dates.map((d, index) => (
            <FlexBox
              gap="8px"
              mt={errors?.dates?.[index - 1]?.startDate || errors?.dates?.[index - 1]?.endDate ? 3 : 1}
              key={index}
              styles="position: relative"
            >
              <Controller
                render={({ field, fieldState }) => (
                  <MonthYearInput
                    {...field}
                    label="Start month"
                    error={fieldState.error?.message || !!fieldState.error}
                    backgroundColor={bgGray}
                    hideFormatLabel
                  />
                )}
                name={`dates.${index}.startDate`}
                control={control}
                rules={{
                  validate: (v) => {
                    if (!v || v.length !== 7 || v.includes('_') || Number(v.split('/')[0]) > 12)
                      return 'Enter valid date'

                    const startDate = new Date(parseInt(v.split('/')[1]), parseInt(v.split('/')[0]) - 1)
                    const validationEndDate = dates[index]
                      ? new Date(
                          parseInt(dates[index].endDate.split('/')[1]),
                          parseInt(dates[index].endDate.split('/')[0]) - 1
                        )
                      : null

                    if (validationEndDate && differenceInCalendarDays(startDate, validationEndDate) > 0) {
                      return `Start month can’t be after the ${dates[index].endDate}`
                    }

                    return true
                  },
                }}
              />
              <Controller
                render={({ field, fieldState }) => (
                  <MonthYearInput
                    {...field}
                    label="End month"
                    error={fieldState.error?.message || !!fieldState.error}
                    backgroundColor={bgGray}
                    hideFormatLabel
                  />
                )}
                name={`dates.${index}.endDate`}
                control={control}
                rules={{
                  validate: (v) => {
                    if (!v || v.length !== 7 || v.includes('_') || Number(v.split('/')[0]) > 12)
                      return 'Enter valid date'

                    const endDate = new Date(parseInt(v.split('/')[1]), parseInt(v.split('/')[0]) - 1)
                    if (differenceInCalendarDays(endDate, new Date()) > 0) {
                      return 'End month can’t be in future'
                    }

                    return true
                  },
                }}
              />
              {index !== 0 && <StyledHighlightOffIcon onClick={() => handleremoveRow(index)} />}
            </FlexBox>
          ))}

          <Box
            mt={
              errors?.dates?.[(errors?.dates?.length || 0) - 1]?.startDate ||
              errors?.dates?.[(errors?.dates?.length || 0) - 1]?.endDate
                ? 4
                : 2
            }
          />
          <Button variant="text" onClick={handleAddRow} icon={<AddCircleIcon />}>
            Add
          </Button>

          <Box mt={8} />
          <Button margin="0 auto" variant="primary" size="large" type="submit" isLoading={isLoading}>
            Change
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Wrap>
      </form>
    </SimpleDialog>
  )
}

const StyledHighlightOffIcon = styled(HighlightOffIcon)`
  color: ${lightGray2};
  transition: color 0.3s !important;
  cursor: pointer;
  position: absolute;
  right: -30px;
  top: 16px;

  &:hover {
    color: ${midGray};
  }
`
